/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Button, Paper, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import ContentShell from "components/ContentShell";
import { SimpleTextInput } from "components/FormTextInput";
import StatusBadge from "components/StatusBadge";
import { useSnackbar } from "hooks/useSnackbar";
import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getTransactionInfo } from "services/reportsService";
import GoalScorersModal from "./components/GoalScorersModal";
import MatchEventsTimeline from "./components/MatchEventsTimeline";

export const getNestedValue = (obj, path, defaultValue = null) => {
  if (!obj || !path) return defaultValue;

  const val = path.split(".").reduce((acc, key) => {
    if (acc && acc[key] !== undefined) {
      return acc[key];
    }
    return defaultValue;
  }, obj);

  // Handle boolean values explicitly
  if (typeof val === "boolean") {
    return val ? "true" : "false";
  }

  return val ?? defaultValue; // Ensure nullish values are replaced with defaultValue
};

const styles = makeStyles((theme) => ({
  root: {
    display: "flex",
  },
  actionsContainer: {
    display: "flex",
    height: 40,
    gap: 10,
  },
  detailContainer: {
    background: "#fff",
    borderRadius: 12,
    width: "100%",
  },
  detailText: {
    fontWeight: 600,
    fontSize: 18,
  },
  row: {
    display: "flex",
    justifyContent: "space-between",
  },
  lightBackground: {
    background: "#e0e0e0",
  },
  leftCell: {
    width: "50%",
    textAlign: "left",
    fontWeight: 500,
    padding: "6px 0 6px 6px",
  },
  rightCell: {
    width: "50%",
    textAlign: "left",
    padding: "6px 0 6px 6px",
  },
}));

const groupedDetails = {
  // Transaction Details
  Transaction_Details: [
    { id: "_id", label: "DB Id (RGS game_rounds)" },
    { id: "betTxnId", label: "Internal Bet Id" },
    { id: "platformTransactionId", label: "External Bet ticket ID" },
    { id: "transactionType", label: "Transaction Type" },
    { id: "playerCurrencyCode", label: "User Currency" },
    { id: "betAmount", label: "Bet Amount" },
    { id: "winAmount", label: "Win Amount" },
    { id: "balance", label: "Balance" },
    { id: "status", label: "Status" },
    { id: "requestItemId", label: "Bet Request item ID" },
    { id: "numericRoundId", label: "Numeric round ID" },
    { id: "resolved", label: "Bet Status" },
  ],

  // Dates
  Dates: [
    { id: "betPlacedAt", label: "Bet placed Date" },
    { id: "betSettledAt", label: "Bet settled Date" },
    { id: "betRefundedAt", label: "Bet refunded Date" },
    { id: "gameBeBetInfo.startDate", label: "Match Start Date" },
    { id: "gameBeBetInfo.endDate", label: "Match End Date" },
  ],

  // Match Info
  Match_Info: [
    { id: "roundId", label: "Match Id" },
    { id: "gameBeBetInfo.matchStatus", label: "Match Status" },
    { id: "gameBeBetInfo.myTeamScore", label: "My Team Score" },
    { id: "gameBeBetInfo.oppTeamScore", label: "Opponent Team Score" },
  ],

  // Division Info
  Division_Info: [
    { id: "gameBeBetInfo.leagueInfo.currentLeague", label: "Division" },
    { id: "gameBeBetInfo.leagueInfo.currentMilestone", label: "Milestone" },
    { id: "gameBeBetInfo.leagueInfo.currentStep", label: "Step" },
    { id: "gameBeBetInfo.leagueInfo.initialOvr", label: "Initial OVR" },
  ],
  // RNG Info
  RNG_Info: [
    { id: "gameBeBetInfo.scorePickRNG", label: "Score Pick RNG" },
    {
      id: "gameBeBetInfo.extraData.selectKValue.randomNumber",
      label: "K Value Pick RNG",
    },
    {
      id: "gameBeBetInfo.extraData.selectKValue.kValue",
      label: "K Value Picked",
    },
  ],

  // My Team Info
  My_Team_Info: [
    { id: "gameBeBetInfo.myTeam.autoName", label: "My Team Unique ID" },
    { id: "gameBeBetInfo.myTeam.attackRating", label: "My Team ATT" },
    { id: "gameBeBetInfo.myTeam.defenseRating", label: "My Team DEF" },
    { id: "gameBeBetInfo.myTeam.creativityRating", label: "My Team CRE" },
    { id: "gameBeBetInfo.myTeam.overallRating", label: "My Team OVR" },
    { id: "gameBeBetInfo.myTeam.chemistry", label: "My Team Chemistry" },
  ],

  // Opponent Team Info
  Opponent_Team_Info: [
    { id: "gameBeBetInfo.opponentTeam._id", label: "Opponent Team DB ID" },
    { id: "gameBeBetInfo.opponentTeam.name", label: "Opponent Team Unique ID" },
    {
      id: "gameBeBetInfo.opponentTeam.attackRating",
      label: "Opponent Team ATT",
    },
    {
      id: "gameBeBetInfo.opponentTeam.defenseRating",
      label: "Opponent Team DEF",
    },
    {
      id: "gameBeBetInfo.opponentTeam.creativityRating",
      label: "Opponent Team CRE",
    },
    {
      id: "gameBeBetInfo.opponentTeam.overallRating",
      label: "Opponent Team OVR",
    },
    {
      id: "gameBeBetInfo.opponentTeam.chemistry",
      label: "Opponent Team Chemistry",
    },
    { id: "gameBeBetInfo.opponentTeam.isBot", label: "Is Bot?" },
    { id: "gameBeBetInfo.opponentTeam.user", label: "GameBe User ID" },
  ],

  // Market Info
  Market_Info: [
    { id: "extraData.oddDetails.name", label: "Market Name" },
    { id: "marketType", label: "Market Type" },
    { id: "extraData.oddDetails.goals", label: "Goals" },
    { id: "extraData.oddDetails.win", label: "Win" },
    { id: "extraData.oddDetails.teamId", label: "Team ID" },
    { id: "extraData.oddDetails.playerId", label: "Player ID" },
    { id: "extraData.oddDetails.team1", label: "Team 1 Score" },
    { id: "extraData.oddDetails.team2", label: "Team 2 Score" },
    { id: "extraData.oddDetails.value", label: "Value" },
    { id: "gameBeBetInfo.searchedBetInfo.oddsDetails.type", label: "Type" },
    {
      id: "gameBeBetInfo.searchedBetInfo.oddsDetails.handicap",
      label: "Handicap",
    },
  ],

  // Odd Info
  Odd_Info: [
    { id: "price", label: "Probability" },
    { id: "oddAfterMargin", label: "Odd" },
    { id: "extraData.oddsId", label: "Odds DB ID" },
    { id: "extraData.oddDetails.operatorMargin", label: "Operator Margin%" },
    { id: "extraData.payoutMultiplier", label: "Payout Multiplier" },
  ],

  // XP Info
  XP_Info: [
    { id: "extraData.xp", label: "XP" },
    { id: "extraData.xpBoostPercentage", label: "XP Boost%" },
  ],

  // Operator Info
  Operator_Info: [
    { id: "operator.operatorName", label: "Operator Name" },
    { id: "operator._id", label: "Internal Operator Id" },
    { id: "operator.externalOpId", label: "External Operator Id" },
  ],

  // User Info
  User_Info: [
    { id: "externalPlayerId", label: "External User Id" },
    { id: "user", label: "RGS User Id" },
    { id: "extraData.userId", label: "GameBE User Id" },
    { id: "playerSessionId", label: "User Session Id" },
    { id: "gameAlias", label: "Game ID" },
    { id: "playerName", label: "Player Name" },
  ],
};

const RoundDetails = () => {
  const classes = styles();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const { search } = useLocation();
  const [loading, setLoading] = React.useState(false);
  const [goalScorersModalOpen, setGoalScorersModalOpen] = React.useState(false);
  const [roundDetails, setRoundDetails] = React.useState({
    betTxnId: new URLSearchParams(search).get("betTxnId") || "",
    info: null,
  });

  const onChange = (e: any) =>
    setRoundDetails((prev) => ({ ...prev, betTxnId: e.target.value }));

  const goBack = () => navigate(-1);

  const fetchData = async (betId) => {
    setLoading(true);
    try {
      const resp = await getTransactionInfo(betId);

      setRoundDetails((prev) => ({
        ...prev,
        info: resp?.data,
      }));
      setLoading(false);
    } catch (error) {
      setRoundDetails((prev) => ({ ...prev, info: null }));
      setLoading(false);
      showSnackbar(error?.message, "error");
    }
  };

  useEffect(() => {
    if (roundDetails.betTxnId) fetchData(roundDetails.betTxnId);
  }, []);

  return (
    <ContentShell
      title="Round Details"
      loading={loading}
      showBackButton={true}
      onBackButtonClick={goBack}
      actions={
        <div className={classes.actionsContainer}>
          <Box sx={{ width: 280 }}>
            <SimpleTextInput
              placeholder="Enter BetTxn ID or Bet ticket ID"
              name={"betTxnId"}
              value={roundDetails.betTxnId}
              onChange={onChange}
              customStyle={{ height: 35 }}
            />
          </Box>
          <Button
            sx={{ height: 35 }}
            variant="contained"
            onClick={() => fetchData(roundDetails.betTxnId)}
          >
            Get Info
          </Button>
        </div>
      }
    >
      <div className={classes.root}>
        {roundDetails.info && (
          <Box className={classes.detailContainer}>
            {Object.entries(groupedDetails).map(([sectionTitle, columns]) => (
              <Paper
                elevation={4}
                key={sectionTitle}
                style={{ margin: 14, padding: 14 }}
              >
                <Box
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 12,
                  }}
                >
                  <Typography variant="h6" fontWeight={"bold"}>
                    {sectionTitle?.replace("_", " ")}
                  </Typography>
                  {sectionTitle === "Match_Info" &&
                    (roundDetails.info?.gameBeBetInfo?.myTeamGoalScorers
                      ?.length > 0 ||
                      roundDetails.info?.gameBeBetInfo?.oppTeamGoalScorers
                        ?.length > 0) && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setGoalScorersModalOpen(true)}
                        sx={{ textTransform: "none" }}
                      >
                        View Goal Scorers
                      </Button>
                    )}
                </Box>
                <Box style={{ display: "flex", gap: 10, flexFlow: "row wrap" }}>
                  {columns.map((column) => (
                    <Box
                      key={column.id}
                      style={{ display: "flex", flexFlow: "column wrap" }}
                    >
                      <Paper
                        elevation={2}
                        sx={{ p: 3, gap: 10, height: "100%" }}
                      >
                        <Typography variant="subtitle2" color="text.secondary">
                          {column.label || column.id}
                        </Typography>
                        {column.id === "resolved" ? (
                          <StatusBadge
                            value={getNestedValue(
                              roundDetails.info,
                              column?.id
                            )}
                            type="resolved"
                          />
                        ) : column.id === "status" ? (
                          <StatusBadge
                            value={getNestedValue(
                              roundDetails.info,
                              column?.id
                            )}
                            type="roundStatus"
                          />
                        ) : column.id === "extraData.oddDetails.playerId" ? (
                          <Box>
                            <Typography fontWeight={"bold"} variant="body2">
                              {getNestedValue(roundDetails.info, column?.id) ??
                                "--"}
                            </Typography>
                            {getNestedValue(roundDetails.info, column?.id) &&
                              roundDetails.info?.gameBeBetInfo?.allPlayers && (
                                <Typography
                                  variant="body2"
                                  color="primary"
                                  display="block"
                                  sx={{ mt: 0.5, fontWeight: "500" }}
                                >
                                  {roundDetails.info.gameBeBetInfo.allPlayers.find(
                                    (p: any) =>
                                      p.playerId ===
                                      getNestedValue(
                                        roundDetails.info,
                                        column?.id
                                      )
                                  )?.name || "Player not found"}
                                </Typography>
                              )}
                          </Box>
                        ) : (
                          <Typography fontWeight={"bold"} variant="body2">
                            {getNestedValue(roundDetails.info, column?.id) ??
                              "--"}
                          </Typography>
                        )}
                      </Paper>
                    </Box>
                  ))}
                </Box>
              </Paper>
            ))}

            {/* Match Events/Slots Section */}
            <MatchEventsTimeline
              slots={roundDetails.info?.gameBeBetInfo?.slots || []}
              allPlayers={roundDetails.info?.gameBeBetInfo?.allPlayers || []}
            />
          </Box>
        )}
      </div>

      {/* Goal Scorers Modal */}
      <GoalScorersModal
        open={goalScorersModalOpen}
        onClose={() => setGoalScorersModalOpen(false)}
        myTeamGoalScorers={
          roundDetails.info?.gameBeBetInfo?.myTeamGoalScorers || []
        }
        oppTeamGoalScorers={
          roundDetails.info?.gameBeBetInfo?.oppTeamGoalScorers || []
        }
      />
    </ContentShell>
  );
};

export default RoundDetails;
